import {useEffect, useMemo, useState} from "react";

const bucketName = import.meta.env.VITE_STATS_BUCKET ?? "waitlist-watcher-scheduled-query-outputs";
const etagCache = new Map<string, string>();
const dataCache = new Map<string, unknown[]>();

export type TimePeriod = "24h" | "7d" | "semester";

type OverviewRow = {
    generated_at?: string;
    semester?: string;
    events_semester?: number | string;
    events_24h?: number | string;
    events_7d?: number | string;
    active_sections_semester?: number | string;
    active_sections_24h?: number | string;
    active_sections_7d?: number | string;
    open_seat_alerts_semester?: number | string;
    open_seat_alerts_24h?: number | string;
    open_seat_alerts_7d?: number | string;
    waitlist_drops_semester?: number | string;
    waitlist_drops_24h?: number | string;
    waitlist_drops_7d?: number | string;
    active_departments_semester?: number | string;
    active_departments_24h?: number | string;
    active_departments_7d?: number | string;
};

export type OverviewStats = {
    generatedAt: string;
    eventsSemester: number;
    events24h: number;
    events7d: number;
    activeSectionsSemester: number;
    activeSections24h: number;
    activeSections7d: number;
    openSeatAlertsSemester: number;
    openSeatAlerts24h: number;
    openSeatAlerts7d: number;
    waitlistDropsSemester: number;
    waitlistDrops24h: number;
    waitlistDrops7d: number;
    activeDepartmentsSemester: number;
    activeDepartments24h: number;
    activeDepartments7d: number;
};

export type TopCourseRow = {
    semester: string;
    period: TimePeriod;
    department: string;
    course: string;
    events?: number | string;
    open_seat_alerts?: number | string;
    seat_churn?: number | string;
    waitlist_churn?: number | string;
};

export type WaitlistedCourseRow = {
    semester: string;
    department: string;
    course: string;
    total_waitlist?: number | string;
    sections?: number | string;
    max_section_waitlist?: number | string;
};

export type WaitlistedSectionRow = {
    semester: string;
    department: string;
    course: string;
    section: string;
    waitlist?: number | string;
    holdfile?: number | string;
    open_seats?: number | string;
    total_seats?: number | string;
    waitlist_ratio?: number | string;
};

export type FastestFillingSectionRow = {
    semester: string;
    period: TimePeriod;
    department: string;
    course: string;
    section: string;
    events?: number | string;
    seats_filled?: number | string;
    seat_churn?: number | string;
};

export type QuickestFilledSectionRow = {
    semester: string;
    department: string;
    course: string;
    section: string;
    quickest_minutes?: number | string;
    events?: number | string;
};

type StatsData = {
    loading: boolean;
    error?: string;
    overview?: OverviewStats;
    topCourses: TopCourseRow[];
    mostWaitlistedCourses: WaitlistedCourseRow[];
    mostWaitlistedSections: WaitlistedSectionRow[];
    fastestFillingSections: FastestFillingSectionRow[];
    quickestFilledSections: QuickestFilledSectionRow[];
};

function parseNumber(value: number | string | undefined): number {
    if (typeof value === "number") return value;
    if (typeof value === "string") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    }
    return 0;
}

async function fetchExportJson<T>(objectPath: string): Promise<T[]> {
    const objectUrl = `https://storage.googleapis.com/storage/v1/b/${encodeURIComponent(bucketName)}/o/${encodeURIComponent(objectPath)}?alt=media`;
    const cachedEtag = etagCache.get(objectPath);
    const headers: Record<string, string> = {};
    if (cachedEtag) {
        headers["If-None-Match"] = cachedEtag;
    }

    const objectRes = await fetch(objectUrl, {
        cache: "no-cache",
        headers,
    });
    if (objectRes.status === 304) {
        return (dataCache.get(objectPath) as T[] | undefined) ?? [];
    }
    if (!objectRes.ok) {
        if (objectRes.status === 404) return [];
        throw new Error(`Bucket object failed for ${objectPath}: ${objectRes.status}`);
    }

    const raw = await objectRes.text();
    const trimmed = raw.trim();
    if (!trimmed) {
        return [];
    }

    try {
        const parsed = JSON.parse(trimmed);
        const rows = Array.isArray(parsed) ? parsed as T[] : [parsed as T];
        const etag = objectRes.headers.get("etag") ?? undefined;
        if (etag) etagCache.set(objectPath, etag);
        dataCache.set(objectPath, rows as unknown[]);
        return rows;
    } catch {
        const rows = trimmed
            .split("\n")
            .map(line => line.trim())
            .filter(Boolean)
            .map(line => JSON.parse(line) as T);
        const etag = objectRes.headers.get("etag") ?? undefined;
        if (etag) etagCache.set(objectPath, etag);
        dataCache.set(objectPath, rows as unknown[]);
        return rows;
    }
}

export function useBucketStats(semesterId: string): StatsData {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const [overview, setOverview] = useState<OverviewStats>();
    const [topCourses, setTopCourses] = useState<TopCourseRow[]>([]);
    const [mostWaitlistedCourses, setMostWaitlistedCourses] = useState<WaitlistedCourseRow[]>([]);
    const [mostWaitlistedSections, setMostWaitlistedSections] = useState<WaitlistedSectionRow[]>([]);
    const [fastestFillingSections, setFastestFillingSections] = useState<FastestFillingSectionRow[]>([]);
    const [quickestFilledSections, setQuickestFilledSections] = useState<QuickestFilledSectionRow[]>([]);

    useEffect(() => {
        let cancelled = false;

        async function load() {
            setLoading(true);
            setError(undefined);
            try {
                const [overviewRows, topCourseRows, waitlistedRows, waitlistedSectionRows, fastestFillingSectionRows, quickestFilledSectionRows] = await Promise.all([
                    fetchExportJson<OverviewRow>(`stats/overview/${semesterId}/overview-000000000000.json`),
                    fetchExportJson<TopCourseRow>(`stats/top_courses/${semesterId}/top-courses-000000000000.json`),
                    fetchExportJson<WaitlistedCourseRow>(`stats/most_waitlisted_courses/${semesterId}/most-waitlisted-courses-000000000000.json`),
                    fetchExportJson<WaitlistedSectionRow>(`stats/most_waitlisted_sections/${semesterId}/most-waitlisted-sections-000000000000.json`),
                    fetchExportJson<FastestFillingSectionRow>(`stats/fastest_filling_sections/${semesterId}/fastest-filling-sections-000000000000.json`),
                    fetchExportJson<QuickestFilledSectionRow>(`stats/quickest_filled_sections/${semesterId}/quickest-filled-sections-000000000000.json`),
                ]);

                if (cancelled) return;

                const first = overviewRows.find(row => String(row.semester) === semesterId);
                setOverview(first ? {
                    generatedAt: first.generated_at ?? "",
                    eventsSemester: parseNumber(first.events_semester),
                    events24h: parseNumber(first.events_24h),
                    events7d: parseNumber(first.events_7d),
                    activeSectionsSemester: parseNumber(first.active_sections_semester),
                    activeSections24h: parseNumber(first.active_sections_24h),
                    activeSections7d: parseNumber(first.active_sections_7d),
                    openSeatAlertsSemester: parseNumber(first.open_seat_alerts_semester),
                    openSeatAlerts24h: parseNumber(first.open_seat_alerts_24h),
                    openSeatAlerts7d: parseNumber(first.open_seat_alerts_7d),
                    waitlistDropsSemester: parseNumber(first.waitlist_drops_semester),
                    waitlistDrops24h: parseNumber(first.waitlist_drops_24h),
                    waitlistDrops7d: parseNumber(first.waitlist_drops_7d),
                    activeDepartmentsSemester: parseNumber(first.active_departments_semester),
                    activeDepartments24h: parseNumber(first.active_departments_24h),
                    activeDepartments7d: parseNumber(first.active_departments_7d),
                } : undefined);

                setTopCourses(topCourseRows);
                setMostWaitlistedCourses(waitlistedRows);
                setMostWaitlistedSections(waitlistedSectionRows);
                setFastestFillingSections(fastestFillingSectionRows);
                setQuickestFilledSections(quickestFilledSectionRows);
            } catch (err) {
                if (cancelled) return;
                setError(err instanceof Error ? err.message : "Failed to load stats");
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        }

        void load();
        const intervalId = window.setInterval(() => {
            void load();
        }, 60_000);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [semesterId]);

    return useMemo(() => ({
        loading,
        error,
        overview,
        topCourses: topCourses
            .filter(row => String(row.semester) === semesterId)
            .sort((a, b) => parseNumber(b.events) - parseNumber(a.events)),
        mostWaitlistedCourses: mostWaitlistedCourses
            .filter(row => String(row.semester) === semesterId)
            .sort((a, b) => parseNumber(b.total_waitlist) - parseNumber(a.total_waitlist)),
        mostWaitlistedSections: mostWaitlistedSections
            .filter(row => String(row.semester) === semesterId)
            .sort((a, b) => parseNumber(b.waitlist) - parseNumber(a.waitlist)),
        fastestFillingSections: fastestFillingSections
            .filter(row => String(row.semester) === semesterId)
            .sort((a, b) => parseNumber(b.seats_filled) - parseNumber(a.seats_filled)),
        quickestFilledSections: quickestFilledSections
            .filter(row => String(row.semester) === semesterId)
            .sort((a, b) => parseNumber(a.quickest_minutes) - parseNumber(b.quickest_minutes)),
    }), [loading, error, overview, topCourses, mostWaitlistedCourses, mostWaitlistedSections, fastestFillingSections, quickestFilledSections, semesterId]);
}
